"use strict";
/**
 * Utility functions for generating tracked links.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.generateJoinEESocialCommunityLink = exports.generatePlugshareReviewLink = exports.generateGoogleReviewLink = exports.generateTrackedLink = exports.getBaseUrlForEnvironment = exports.TrackedLinkFormat = exports.TrackedLinkSource = exports.MIN_ENERGY_FOR_REVIEW_KWH = void 0;
/**
 * Minimum energy threshold (in kWh) required before showing review CTA.
 * Reviews are only shown for sessions that delivered more than this amount.
 */
exports.MIN_ENERGY_FOR_REVIEW_KWH = 0.01;
/**
 * Source of the tracked link generation.
 */
var TrackedLinkSource;
(function (TrackedLinkSource) {
    /** Link generated from instructions screen */
    TrackedLinkSource["CUI_INSTRUCTIONS_SCREEN"] = "cui-instructions-screen";
    /** Link generated from charger UI receipt */
    TrackedLinkSource["CUI_RECEIPT"] = "cui-receipt";
    /** Link generated from email receipt */
    TrackedLinkSource["EMAIL_RECEIPT"] = "email-receipt";
})(TrackedLinkSource = exports.TrackedLinkSource || (exports.TrackedLinkSource = {}));
/**
 * Format of the tracked link presentation.
 */
var TrackedLinkFormat;
(function (TrackedLinkFormat) {
    /** QR code format */
    TrackedLinkFormat["QR"] = "qr";
    /** Direct link format */
    TrackedLinkFormat["LINK"] = "link";
})(TrackedLinkFormat = exports.TrackedLinkFormat || (exports.TrackedLinkFormat = {}));
/**
 * Gets the base URL for tracked links based on the environment.
 *
 * @param environment - The environment string ('DEV', 'PROD', etc.)
 * @returns The appropriate base URL
 *
 * @example
 * ```typescript
 * const baseUrl = getBaseUrlForEnvironment('DEV');
 * // Returns: 'https://dev.eera.app/dl'
 *
 * const prodUrl = getBaseUrlForEnvironment('PROD');
 * // Returns: 'https://eera.app/dl'
 * ```
 */
function getBaseUrlForEnvironment(environment) {
    const env = environment?.toUpperCase();
    switch (env) {
        case "DEV":
        case "DEVELOPMENT":
            return "https://dev.eera.app/dl";
        case "PROD":
        case "PRODUCTION":
        default:
            return "https://eera.app/dl";
    }
}
exports.getBaseUrlForEnvironment = getBaseUrlForEnvironment;
/**
 * Generates a tracked link URL in the format:
 * {baseUrl}/{slug}?url={destinationUrl}&et={entityType}&eid={entityId}
 *
 * @param options - The options for generating the tracked link
 * @returns The complete tracked link URL
 *
 * @example
 * ```typescript
 * const link = generateTrackedLink({
 *   slug: 'google-review',
 *   destinationUrl: 'https://maps.google.com/place/abc/review',
 *   entityType: 'review',
 *   entityId: 123
 * });
 * // Returns: 'https://eera.app/dl/google-review-123?url=https%3A//maps.google.com/place/abc/review&et=review&eid=123'
 * ```
 */
function generateTrackedLink(options) {
    const { slug, destinationUrl, entityType, entityId, baseUrl = "https://eera.app/dl", } = options;
    const params = new URLSearchParams({
        url: destinationUrl,
        et: entityType,
        eid: String(entityId),
    });
    return `${baseUrl}/${slug}?${params.toString()}`;
}
exports.generateTrackedLink = generateTrackedLink;
/**
 * Generates a Google review tracked link for a charger.
 *
 * @param chargerOcppId - The charger OCPP ID
 * @param googlePlaceId - The Google Place ID
 * @param source - The source of the link generation
 * @param format - The format of the link presentation
 * @param baseUrl - Optional base URL (defaults to environment-specific URL)
 * @returns The complete Google review tracked link URL
 *
 * @example
 * ```typescript
 * const link = generateGoogleReviewLink(
 *   'charger-001',
 *   'ChIJN1t_tDeuEmsRUsoyG83frY4',
 *   TrackedLinkSource.CUI_RECEIPT,
 *   TrackedLinkFormat.QR
 * );
 * // Returns: 'https://eera.app/dl/google-review?url=...&et=chargerOcppId&eid=charger-001&src=cui-receipt&fmt=qr'
 * ```
 */
function generateGoogleReviewLink(chargerOcppId, googlePlaceId, source, format, baseUrl) {
    const effectiveBaseUrl = baseUrl ||
        getBaseUrlForEnvironment(process.env.ENVIRONMENT || process.env.NODE_ENV);
    const params = new URLSearchParams({
        url: `https://search.google.com/local/writereview?placeid=${googlePlaceId}`,
        et: "chargerOcppId",
        eid: chargerOcppId,
        src: source,
        fmt: format,
    });
    return `${effectiveBaseUrl}/google-review?${params.toString()}`;
}
exports.generateGoogleReviewLink = generateGoogleReviewLink;
/**
 * Generates a PlugShare review tracked link for a charger.
 *
 * @param chargerOcppId - The charger OCPP ID
 * @param plugshareId - The PlugShare location ID
 * @param source - The source of the link generation
 * @param format - The format of the link presentation
 * @param baseUrl - Optional base URL (defaults to environment-specific URL)
 * @returns The complete PlugShare review tracked link URL
 *
 * @example
 * ```typescript
 * const link = generatePlugshareReviewLink(
 *   'charger-001',
 *   '456789',
 *   TrackedLinkSource.CUI_RECEIPT,
 *   TrackedLinkFormat.QR
 * );
 * // Returns: 'https://eera.app/dl/plugshare-review?url=...&et=chargerOcppId&eid=charger-001&src=cui-receipt&fmt=qr'
 * ```
 */
function generatePlugshareReviewLink(chargerOcppId, plugshareId, source, format, baseUrl) {
    const effectiveBaseUrl = baseUrl ||
        getBaseUrlForEnvironment(process.env.ENVIRONMENT || process.env.NODE_ENV);
    const params = new URLSearchParams({
        url: `https://www.plugshare.com/location/${plugshareId}`,
        et: "chargerOcppId",
        eid: chargerOcppId,
        src: source,
        fmt: format,
    });
    return `${effectiveBaseUrl}/plugshare-review?${params.toString()}`;
}
exports.generatePlugshareReviewLink = generatePlugshareReviewLink;
/**
 * Generates a join ee social community link.
 *
 * @param options - The set of options for generating the Join EE Community link.
 * @returns The complete Join EE Social Community tracked link URL
 *
 * @example
 * ```typescript
 * const link = generateJoinEESocialCommunityLink({
 *   entityId: 'charger-001',
 *   entityTtype: 'chargerOcppId'
 *   source: TrackedLinkSource.CUI_INSTRUCTIONS_SCREEN,
 *   format: TrackedLinkFormat.QR
 * });
 * // Returns: 'https://eera.app/dl/join-community?url=https://x.com/i/communities/1965168374130114706&et=chargerOcppId&eid=charger-001&src=cui-receipt&fmt=qr'
 * ```
 */
function generateJoinEESocialCommunityLink(options) {
    const effectiveBaseUrl = options.baseUrl ??
        getBaseUrlForEnvironment(process.env.ENVIRONMENT || process.env.NODE_ENV);
    const params = new URLSearchParams({
        url: "https://x.com/i/communities/1965168374130114706",
        et: options.entityType,
        eid: options.entityId,
        src: options.source,
        fmt: options.format,
    });
    return `${effectiveBaseUrl}/join-community?${params.toString()}`;
}
exports.generateJoinEESocialCommunityLink = generateJoinEESocialCommunityLink;
